import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShowcaseAPI,
  PlayersAPI,
  TeamsAPI,
  TournamentsAPI,
  ChampionshipsAPI,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteHeader from "@/components/SiteHeader";
import ImageInput from "@/components/ImageInput";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, ExternalLink } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const ICONS = [
  { value: "user", label: "Joueur" },
  { value: "trophy", label: "Trophée" },
  { value: "shield", label: "Bouclier / Équipe" },
  { value: "crown", label: "Couronne / Championnat" },
  { value: "medal", label: "Médaille" },
  { value: "sparkles", label: "Étincelles" },
  { value: "scan", label: "Scan QR" },
  { value: "wifi", label: "NFC / Wifi" },
  { value: "layers", label: "Matière / Couches" },
  { value: "pen", label: "Personnalisation" },
  { value: "check", label: "Coche" },
];

const LINK_TYPES = [
  { value: "player", label: "Page joueur", prefix: "/player/" },
  { value: "team", label: "Page équipe", prefix: "/team/" },
  { value: "tournament", label: "Page tournoi", prefix: "/tournament/" },
  { value: "championship", label: "Page championnat", prefix: "/championship/" },
  { value: "custom", label: "URL libre", prefix: "" },
];

const detectLinkType = (url) => {
  for (const t of LINK_TYPES) {
    if (t.prefix && (url || "").startsWith(t.prefix)) return t.value;
  }
  return "custom";
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-overline !text-zinc-400">{label}</Label>
    {children}
  </div>
);

export default function AdminShowcase() {
  const [cards, setCards] = useState(null);
  const [content, setContent] = useState(null);
  const [entities, setEntities] = useState({
    player: [],
    team: [],
    tournament: [],
    championship: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    ShowcaseAPI.get()
      .then((d) => setCards(d.cards || []))
      .catch(() => toast.error("Impossible de charger les cartes"));
    ShowcaseAPI.getContent()
      .then((d) => setContent(d.content))
      .catch(() => toast.error("Impossible de charger le contenu"));
    PlayersAPI.list().then((l) => setEntities((e) => ({ ...e, player: l }))).catch(() => {});
    TeamsAPI.list().then((l) => setEntities((e) => ({ ...e, team: l }))).catch(() => {});
    TournamentsAPI.list().then((l) => setEntities((e) => ({ ...e, tournament: l }))).catch(() => {});
    ChampionshipsAPI.list().then((l) => setEntities((e) => ({ ...e, championship: l }))).catch(() => {});
  }, []);

  const update = (i, patch) =>
    setCards((c) => c.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const remove = (i) => setCards((c) => c.filter((_, idx) => idx !== i));

  // ----- content helpers -----
  const setSec = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));
  const setSecItem = (section, i, patch) =>
    setContent((c) => ({
      ...c,
      [section]: {
        ...c[section],
        items: c[section].items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)),
      },
    }));
  const addSecItem = (section, item) =>
    setContent((c) => ({
      ...c,
      [section]: { ...c[section], items: [...(c[section].items || []), item] },
    }));
  const removeSecItem = (section, i) =>
    setContent((c) => ({
      ...c,
      [section]: { ...c[section], items: c[section].items.filter((_, idx) => idx !== i) },
    }));
  const add = () =>
    setCards((c) => [
      ...c,
      {
        id: crypto.randomUUID(),
        title: "",
        description: "",
        bullets: [],
        icon: "medal",
        image_url: "",
        accent: "#D4AF37",
        button_label: "Voir la page",
        link_url: "",
      },
    ]);

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = cards.map((c) =>
        LINK_TYPES.some((t) => t.prefix && c.link_url === t.prefix)
          ? { ...c, link_url: "" }
          : c,
      );
      const [d, dc] = await Promise.all([
        ShowcaseAPI.update(cleaned),
        ShowcaseAPI.updateContent(content),
      ]);
      setCards(d.cards);
      setContent(dc.content);
      toast.success("Page « Découvrir nos médailles » mise à jour");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!cards || !content)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <div className="flex items-center justify-between gap-4 mb-6">
          <Link
            to="/admin/settings"
            data-testid="showcase-admin-back"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body"
          >
            <ArrowLeft className="w-3 h-3" /> Retour
          </Link>
          <a
            href="/showcase"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="showcase-admin-preview"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white border border-white/15 bg-white/[0.04] rounded-full px-3 py-1.5 text-xs uppercase tracking-[0.18em] font-bold"
          >
            <ExternalLink className="w-3 h-3" /> Voir la page
          </a>
        </div>

        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-2">
          Page <span style={{ color: "#D4AF37" }}>Découvrir</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          Personnalise toute la page : textes du héro, cartes produits, étapes,
          atouts, FAQ et formulaire — sans toucher au code.
        </p>

        {/* ================= HÉRO ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Héro (haut de page)
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-hero-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Surtitre">
              <Input className={inputClass} value={content.hero.overline} onChange={(e) => setSec("hero", "overline", e.target.value)} data-testid="hero-overline-input" />
            </Field>
            <Field label="Bouton principal (devis)">
              <Input className={inputClass} value={content.hero.cta_primary} onChange={(e) => setSec("hero", "cta_primary", e.target.value)} data-testid="hero-cta-primary-input" />
            </Field>
            <Field label="Titre — ligne 1">
              <Input className={inputClass} value={content.hero.title_line1} onChange={(e) => setSec("hero", "title_line1", e.target.value)} data-testid="hero-title1-input" />
            </Field>
            <Field label="Titre — ligne 2 (dorée)">
              <Input className={inputClass} value={content.hero.title_line2} onChange={(e) => setSec("hero", "title_line2", e.target.value)} data-testid="hero-title2-input" />
            </Field>
          </div>
          <Field label="Sous-titre / description">
            <Textarea className={`${inputClass} min-h-[80px]`} value={content.hero.subtitle} onChange={(e) => setSec("hero", "subtitle", e.target.value)} data-testid="hero-subtitle-input" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Bouton secondaire">
              <Input className={inputClass} value={content.hero.cta_secondary} onChange={(e) => setSec("hero", "cta_secondary", e.target.value)} />
            </Field>
            <Field label="Badges (un par ligne)">
              <Textarea className={`${inputClass} min-h-[60px]`} value={(content.hero.badges || []).join("\n")} onChange={(e) => setSec("hero", "badges", e.target.value.split("\n"))} />
            </Field>
            <Field label="Image / affiche de droite (vide = logo Laser Tech)">
              <ImageInput value={content.hero.side_image_url || ""} onChange={(v) => setSec("hero", "side_image_url", v)} testId="hero-side-image" size="sm" />
            </Field>
            <Field label="Légende sous l'image">
              <Input className={inputClass} value={content.hero.side_caption} onChange={(e) => setSec("hero", "side_caption", e.target.value)} />
            </Field>
          </div>
        </div>

        {/* ================= EN-TÊTE GAMME ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Section « La gamme » — en-tête
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-products-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Surtitre">
              <Input className={inputClass} value={content.products.overline} onChange={(e) => setSec("products", "overline", e.target.value)} />
            </Field>
            <Field label="Titre">
              <Input className={inputClass} value={content.products.title} onChange={(e) => setSec("products", "title", e.target.value)} data-testid="products-title-input" />
            </Field>
          </div>
          <Field label="Sous-titre">
            <Textarea className={`${inputClass} min-h-[60px]`} value={content.products.subtitle} onChange={(e) => setSec("products", "subtitle", e.target.value)} />
          </Field>
        </div>

        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Les cartes produits
        </h2>
        <div className="space-y-6">
          {cards.map((card, i) => {
            const linkType = detectLinkType(card.link_url);
            const typeDef = LINK_TYPES.find((t) => t.value === linkType);
            return (
              <div
                key={card.id || i}
                className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4"
                data-testid={`showcase-card-editor-${i}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="font-display text-sm font-black uppercase tracking-[0.2em]"
                    style={{ color: card.accent || "#D4AF37" }}
                  >
                    Carte {i + 1}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => remove(i)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`showcase-remove-card-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Titre">
                    <Input
                      className={inputClass}
                      value={card.title}
                      onChange={(e) => update(i, { title: e.target.value })}
                      placeholder="Médaille Joueur"
                      data-testid={`showcase-card-title-${i}`}
                    />
                  </Field>
                  <Field label="Couleur d'accent">
                    <div className="flex gap-2 items-center">
                      <Input
                        type="color"
                        className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                        value={card.accent || "#D4AF37"}
                        onChange={(e) => update(i, { accent: e.target.value })}
                      />
                      <Input
                        className={inputClass}
                        value={card.accent || ""}
                        onChange={(e) => update(i, { accent: e.target.value })}
                      />
                    </div>
                  </Field>
                </div>

                <Field label="Description">
                  <Input
                    className={inputClass}
                    value={card.description}
                    onChange={(e) => update(i, { description: e.target.value })}
                    placeholder="Une médaille personnelle nominative"
                    data-testid={`showcase-card-description-${i}`}
                  />
                </Field>

                <Field label="Fonctionnalités (une par ligne)">
                  <Textarea
                    className={`${inputClass} min-h-[90px]`}
                    value={(card.bullets || []).join("\n")}
                    onChange={(e) =>
                      update(i, {
                        bullets: e.target.value.split("\n").map((b) => b.replace(/\r/g, "")),
                      })
                    }
                    placeholder={"Photo, position, numéro\nStats détaillées\nVidéos highlights"}
                    data-testid={`showcase-card-bullets-${i}`}
                  />
                </Field>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Image (remplace l'icône)">
                    <ImageInput
                      value={card.image_url || ""}
                      onChange={(v) => update(i, { image_url: v })}
                      testId={`showcase-card-image-${i}`}
                      size="sm"
                    />
                  </Field>
                  <Field label="Icône (si pas d'image)">
                    <Select
                      value={card.icon || "medal"}
                      onValueChange={(v) => update(i, { icon: v })}
                    >
                      <SelectTrigger className={inputClass} data-testid={`showcase-card-icon-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111216] border-white/10 text-white">
                        {ICONS.map((ic) => (
                          <SelectItem key={ic.value} value={ic.value}>
                            {ic.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="border-t border-white/5 pt-4 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Texte du bouton">
                      <Input
                        className={inputClass}
                        value={card.button_label}
                        onChange={(e) => update(i, { button_label: e.target.value })}
                        placeholder="Voir une page joueur"
                        data-testid={`showcase-card-button-label-${i}`}
                      />
                    </Field>
                    <Field label="Ouvrir vers">
                      <Select
                        value={linkType}
                        onValueChange={(v) => {
                          const def = LINK_TYPES.find((t) => t.value === v);
                          update(i, { link_url: def?.prefix || "" });
                        }}
                      >
                        <SelectTrigger className={inputClass} data-testid={`showcase-card-linktype-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          {LINK_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  {linkType === "custom" ? (
                    <Field label="Lien du bouton (URL ou chemin interne)">
                      <Input
                        className={inputClass}
                        value={card.link_url || ""}
                        onChange={(e) => update(i, { link_url: e.target.value })}
                        placeholder="/showcase ou https://…"
                        data-testid={`showcase-card-link-${i}`}
                      />
                    </Field>
                  ) : (
                    <Field label={`Choisir ${typeDef?.label?.toLowerCase() || "la page"}`}>
                      <Select
                        value={card.link_url || ""}
                        onValueChange={(v) => update(i, { link_url: v })}
                      >
                        <SelectTrigger className={inputClass} data-testid={`showcase-card-entity-${i}`}>
                          <SelectValue placeholder="Sélectionner…" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white max-h-64">
                          {(entities[linkType] || []).map((ent) => (
                            <SelectItem key={ent.id} value={`${typeDef.prefix}${ent.slug}`}>
                              {ent.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </div>
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            onClick={add}
            data-testid="showcase-add-card"
            className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Ajouter une carte
          </Button>
        </div>

        {/* ================= ÉTAPES ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mt-10 mb-4 text-[#D4AF37]">
          Comment ça marche (étapes)
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-steps-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Surtitre">
              <Input className={inputClass} value={content.steps.overline} onChange={(e) => setSec("steps", "overline", e.target.value)} />
            </Field>
            <Field label="Titre">
              <Input className={inputClass} value={content.steps.title} onChange={(e) => setSec("steps", "title", e.target.value)} data-testid="steps-title-input" />
            </Field>
          </div>
          <Field label="Image / affiche de la section">
            <ImageInput value={content.steps.image_url || ""} onChange={(v) => setSec("steps", "image_url", v)} testId="steps-image" size="sm" />
          </Field>
          {(content.steps.items || []).map((s, i) => (
            <div key={i} className="grid sm:grid-cols-[140px,1fr,1.4fr,40px] gap-2 items-start border-t border-white/5 pt-3" data-testid={`step-item-${i}`}>
              <Select value={s.icon || "sparkles"} onValueChange={(v) => setSecItem("steps", i, { icon: v })}>
                <SelectTrigger className={`${inputClass} h-10`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  {ICONS.map((ic) => (<SelectItem key={ic.value} value={ic.value}>{ic.label}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input className={inputClass} placeholder="Titre de l'étape" value={s.title} onChange={(e) => setSecItem("steps", i, { title: e.target.value })} data-testid={`step-title-${i}`} />
              <Input className={inputClass} placeholder="Texte" value={s.text} onChange={(e) => setSecItem("steps", i, { text: e.target.value })} />
              <Button type="button" variant="ghost" onClick={() => removeSecItem("steps", i)} className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => addSecItem("steps", { icon: "sparkles", title: "", text: "" })} data-testid="add-step" className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white">
            <Plus className="w-4 h-4 mr-2" /> Ajouter une étape
          </Button>
        </div>

        {/* ================= MULTISPORT ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Bloc multisport
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-multisport-editor">
          <Field label="Titre">
            <Input className={inputClass} value={content.multisport.title} onChange={(e) => setSec("multisport", "title", e.target.value)} />
          </Field>
          <Field label="Texte">
            <Textarea className={`${inputClass} min-h-[60px]`} value={content.multisport.text} onChange={(e) => setSec("multisport", "text", e.target.value)} />
          </Field>
        </div>

        {/* ================= ATOUTS ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Les atouts (3 blocs)
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-benefits-editor">
          {(content.benefits.items || []).map((b, i) => (
            <div key={i} className="grid sm:grid-cols-[140px,1fr,1.4fr,40px] gap-2 items-start" data-testid={`benefit-item-${i}`}>
              <Select value={b.icon || "layers"} onValueChange={(v) => setSecItem("benefits", i, { icon: v })}>
                <SelectTrigger className={`${inputClass} h-10`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111216] border-white/10 text-white">
                  {ICONS.map((ic) => (<SelectItem key={ic.value} value={ic.value}>{ic.label}</SelectItem>))}
                </SelectContent>
              </Select>
              <Input className={inputClass} placeholder="Titre" value={b.title} onChange={(e) => setSecItem("benefits", i, { title: e.target.value })} />
              <Input className={inputClass} placeholder="Texte" value={b.text} onChange={(e) => setSecItem("benefits", i, { text: e.target.value })} />
              <Button type="button" variant="ghost" onClick={() => removeSecItem("benefits", i)} className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => addSecItem("benefits", { icon: "layers", title: "", text: "" })} className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un atout
          </Button>
        </div>

        {/* ================= FAQ ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          FAQ
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4 mb-10" data-testid="content-faq-editor">
          <Field label="Titre de la section">
            <Input className={inputClass} value={content.faq.title} onChange={(e) => setSec("faq", "title", e.target.value)} />
          </Field>
          {(content.faq.items || []).map((f, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr,1.6fr,40px] gap-2 items-start border-t border-white/5 pt-3" data-testid={`faq-item-${i}`}>
              <Input className={inputClass} placeholder="Question" value={f.q} onChange={(e) => setSecItem("faq", i, { q: e.target.value })} data-testid={`faq-q-${i}`} />
              <Textarea className={`${inputClass} min-h-[60px]`} placeholder="Réponse" value={f.a} onChange={(e) => setSecItem("faq", i, { a: e.target.value })} />
              <Button type="button" variant="ghost" onClick={() => removeSecItem("faq", i)} className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => addSecItem("faq", { q: "", a: "" })} data-testid="add-faq" className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white">
            <Plus className="w-4 h-4 mr-2" /> Ajouter une question
          </Button>
        </div>

        {/* ================= CONTACT ================= */}
        <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 text-[#D4AF37]">
          Formulaire de devis — en-tête
        </h2>
        <div className="bg-[#0A0B0E] border border-white/10 rounded-xl p-4 sm:p-5 space-y-4" data-testid="content-contact-editor">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Surtitre">
              <Input className={inputClass} value={content.contact.overline} onChange={(e) => setSec("contact", "overline", e.target.value)} />
            </Field>
            <Field label="Titre">
              <Input className={inputClass} value={content.contact.title} onChange={(e) => setSec("contact", "title", e.target.value)} />
            </Field>
          </div>
          <Field label="Sous-titre">
            <Input className={inputClass} value={content.contact.subtitle} onChange={(e) => setSec("contact", "subtitle", e.target.value)} />
          </Field>
        </div>

        <div className="sticky bottom-4 z-10 mt-8">
          <div className="bg-[#0A0B0E]/90 backdrop-blur-xl border border-white/10 rounded-xl p-3 flex items-center justify-end">
            <Button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="showcase-save"
              className="text-black rounded-md px-6 h-11 font-body font-medium hover:opacity-90"
              style={{ backgroundColor: "#D4AF37" }}
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
